<template>
    <Transition name="slide-fade">
        <div
            v-if="visible"
            class="notification-container"
            :class="{ 'important': isImportant }"
            @click="dismiss"
        >
            <div class="notification-icon" v-html="icon"></div>
            <div class="notification-content" v-html="message"></div>
            <span v-if="count > 1" class="notification-count">x{{ count }}</span>
            <button v-if="isPersistent" class="notification-close" @click.stop="dismiss"
                    aria-label="Close notification">
                <IconClose/>
            </button>
        </div>
    </Transition>
</template>

<script setup>
import {ref, watch, onMounted, onUnmounted} from 'vue';
import IconClose from "@/components/icons/IconClose.vue";

const props = defineProps({
    icon: {
        type: String,
        default: ''
    },
    message: {
        type: String,
        default: ''
    },
    visible: {
        type: Boolean,
        default: false
    },
    isPersistent: {
        type: Boolean,
        default: false
    },
    isImportant: {
        type: Boolean,
        default: false
    },
    duration: {
        type: Number,
        default: 3000
    },
    count: {
        type: Number,
        default: 1
    }
});

const emit = defineEmits(['dismiss']);

let dismissTimer = null;

// Watch for changes in visibility to set up auto-dismiss
watch(() => props.visible, (newValue) => {
    if (newValue && !props.isPersistent) {
        resetDismissTimer();
    } else {
        clearDismissTimer();
    }
});

// Set up auto-dismiss timer when component mounts
onMounted(() => {
    if (props.visible && !props.isPersistent) {
        resetDismissTimer();
    }
});

// Clean up timer when component unmounts
onUnmounted(() => {
    clearDismissTimer();
});

// Reset the dismiss timer
const resetDismissTimer = () => {
    clearDismissTimer();
    dismissTimer = setTimeout(() => {
        dismiss();
    }, props.duration);
};

// Clear the dismiss timer
const clearDismissTimer = () => {
    if (dismissTimer) {
        clearTimeout(dismissTimer);
        dismissTimer = null;
    }
};

// Dismiss the notification
const dismiss = () => {
    clearDismissTimer();
    emit('dismiss');
};
</script>

<style scoped>
.notification-container {
    position: absolute;
    bottom: var(--spacing-2xl);
    right: var(--spacing-2xl);
    min-width: calc(var(--cqw) * 22);
    max-width: calc(var(--cqw) * 25);
    background-color: var(--color-primary);
    color: var(--color-text);
    text-shadow: var(--shadow-sm);
    font-size: var(--font-size-reg);
    border-radius: var(--border-radius-lg);
    border: var(--border-norm) var(--color-primary-light);
    /* padding: var(--spacing-md); */
    box-shadow: var(--shadow-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-index-notif);
    cursor: pointer; /* Add cursor pointer to indicate it's clickable */
}

.notification-container.important {
    background-color: var(--color-error, #E53935);
    border: var(--border-norm) var(--color-secondary-light);
}

.notification-content {
    flex: 1;
    padding: var(--spacing-xs);
    overflow-wrap: break-word;
    overflow: hidden;
    white-space: normal;
}

.notification-icon {
    padding: var(--spacing-xs);
    justify-content: center;
    align-items: center;
    filter: drop-shadow(var(--shadow-md));
}

.notification-count {
    background-color: var(--color-surface-light);
    border-radius: var(--border-radius-md);
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: var(--font-size-reg);
    margin-left: var(--spacing-sm);
}

.notification-close {
    background: transparent;
    border: none;
    color: white;
    opacity: 0.7;
    cursor: pointer;
    padding: var(--spacing-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity var(--transition-normal);
}

.notification-close:hover {
    opacity: 1;
}

/* Transition animations */
.slide-fade-leave-active, .slide-fade-enter-active {
    transition: var(--transition-notif);
}

.slide-fade-enter-from, .slide-fade-leave-to {
    transform: translateX(100%);
    opacity: 0;
}
</style>
